import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { from, of, switchMap, mergeMap, tap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DsToastService } from '@bmw-ds/components';
import { TranslateService } from '@ngx-translate/core';
import { ReportingService } from '../reporting-form/reporting.service';
import { ReportDownloadService } from './report-download.service';
import { REPORT_FILE_NAMES } from '../reporting.constants';
import {
  generateReport,
  generateReportFailure,
  generateReportSuccess,
  IReportRequestParams,
} from './reporting.actions';
import { ReportType } from './reporting.state';

@Injectable()
export class ReportingEffects {
  private readonly actions$ = inject(Actions);
  private readonly reportingService = inject(ReportingService);
  private readonly downloadService = inject(ReportDownloadService);
  private readonly toastService = inject(DsToastService);
  private readonly translateService = inject(TranslateService);

  public generateReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(generateReport),
      mergeMap(({ reportType, params }) =>
        this.requestReport(reportType, params).pipe(
          switchMap(({ presignedUrl }) =>
            from(
              this.downloadService.download(
                presignedUrl,
                REPORT_FILE_NAMES[reportType]
              )
            ).pipe(map(() => generateReportSuccess({ reportType })))
          ),
          catchError((error) =>
            of(generateReportFailure({ reportType, error }))
          )
        )
      )
    )
  );

  public notifyReportFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(generateReportFailure),
        tap(() =>
          this.toastService.pushToast({
            id: 'generate-report-failure',
            tone: 'critical',
            toastText: this.translateService.instant(
              'reporting.damageDetailsTab.reportErrorMessage'
            ),
          })
        )
      ),
    { dispatch: false }
  );

  private requestReport(reportType: ReportType, params: IReportRequestParams) {
    return reportType === 'claimHandling'
      ? this.reportingService.createClaimHandlingReport(params)
      : this.reportingService.createDamageEntriesReport(params);
  }
}
